import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/contexts/ToastContext';
import type { OrderWithDetails, Table, MenuCategory, MenuItem, Category } from '@/types/common';

interface UseOrderDataReturn {
  order: OrderWithDetails | null;
  tables: Table[];
  menuItems: MenuItem[];
  categories: MenuCategory[];
  subcategories: Category[];
  loading: boolean;
  error: Error | null;
  retry: () => Promise<void>;
  retrying: boolean;
  updateOrderLocally: (order: OrderWithDetails) => void;
}

export function useOrderData(orderId?: string): UseOrderDataReturn {
  const { addToast } = useToast();
  const [order, setOrder] = useState<OrderWithDetails | null>(null);
  const [tables, setTables] = useState<Table[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    fetchData();
  }, [orderId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch order if orderId is provided
      if (orderId) {
        const { data: orderData, error: orderError } = await supabase
          .from('orders')
          .select(`
            *,
            table:tables(*),
            items:order_items(
              *,
              menu_item:menu_items(*)
            )
          `)
          .eq('id', orderId)
          .single();

        if (orderError) throw orderError;
        setOrder(orderData);
      }

      // Fetch tables
      const { data: tablesData, error: tablesError } = await supabase
        .from('tables')
        .select('*')
        .order('number');

      if (tablesError) throw tablesError;
      setTables(tablesData);

      // Fetch categories
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('categories')
        .select('*')
        .order('order_index');

      if (categoriesError) throw categoriesError;

      // Fetch subcategories
      const { data: subcategoriesData, error: subcategoriesError } = await supabase
        .from('subcategories')
        .select('*')
        .order('order_index');

      if (subcategoriesError) throw subcategoriesError;

      // Fetch menu items with their categories and subcategories
      const { data: menuItemsData, error: menuItemsError } = await supabase
        .from('menu_items')
        .select(`
          *,
          category:categories(*),
          subcategory:subcategories(*)
        `)
        .eq('active', true)
        .order('name');

      if (menuItemsError) throw menuItemsError;

      setCategories(categoriesData);
      setSubcategories(subcategoriesData);
      setMenuItems(menuItemsData);
    } catch (err) {
      setError(err as Error);
      console.error('Error fetching order data:', err);
    } finally {
      setLoading(false);
    }
  };

  const retry = async () => {
    setRetrying(true);
    await fetchData();
    setRetrying(false);
  };

  const updateOrderLocally = (updatedOrder: OrderWithDetails) => {
    setOrder(updatedOrder);
  };

  return {
    order,
    tables,
    menuItems,
    categories: categories.map(category => ({
      category,
      items: menuItems.filter(item => item.category_id === category.id)
    })),
    subcategories,
    loading,
    error,
    retry,
    retrying,
    updateOrderLocally
  };
}